import React from 'react';
import { formatDriveCapacity } from 'utils/configUtilities';
import { Viewer } from 'shared/ui/Viewer';
import { DataTable, type Column } from 'shared/ui/DataTable';
import { useAbility, PERM } from 'shared/access';
import { useQuoteActionsCtx } from 'shared/state/quoteActionsContext';
import { useSavedQuotes, type SavedQuote } from './useSavedQuotes';

const cfg = (q: SavedQuote, key: string) => q.config[key];

/** `veloCount` / `hybridNodeCount` / `afaNodeCount` (QuotesModal.js:133). */
const count = (q: SavedQuote, key: string) => Number(cfg(q, key) ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/** `veloSsdCapacity` / `hybridVpodSsdCapacity` / `hybridHddCapacity` / `afaVpodSsdCapacity`,
 *  each suffixed " TB" (QuotesModal.js:136,142,145,151). */
const drive = (q: SavedQuote, key: string) => `${formatDriveCapacity(cfg(q, key) as string | number)} TB`;

function buildColumns(showTotal: boolean, userName: string): Column<SavedQuote>[] {
  const base: Column<SavedQuote>[] = [
    // QuotesModal.js:118 — date AND time, not the date alone.
    { key: 'date', header: 'Date Created', render: (q) => `${q.createdAt.toLocaleDateString()} ${q.createdAt.toLocaleTimeString()}` },
    { key: 'desc', header: 'Description', render: (q) => q.description },
    // QuotesModal.js:124 — `{userEmail || quote.userId}`; userEmail is the signed-in
    // user's name published on the quote-actions context as `userName`.
    { key: 'user', header: 'User', render: (q) => userName || q.userId },
    {
      key: 'type',
      header: 'System Type',
      // QuotesModal.js:127 — 'BaseSystem' -> 'Base', else the value, else 'Base'.
      render: (q) => {
        const type = cfg(q, 'systemType') as string | undefined;
        return type === 'BaseSystem' ? 'Base' : (type || 'Base');
      },
    },
    {
      key: 'raw',
      header: 'RAW Capacity',
      align: 'right',
      // QuotesModal.js:130 — suffixed " PB". `?? 0` guard: a row with missing
      // metrics must render, not throw, unlike the classic component.
      render: (q) => `${Number(q.metrics.totalRawCapacity ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} PB`,
    },
    // QuotesModal.js:132,138,147 — count cells are centered, unlike the
    // capacity/cost cells around them.
    { key: 'dCount', header: 'Director Count', align: 'center', render: (q) => count(q, 'veloCount') },
    { key: 'dSsd', header: 'Director SSD', align: 'right', render: (q) => drive(q, 'veloSsdCapacity') },
    { key: 'hCount', header: 'Hybrid Count', align: 'center', render: (q) => count(q, 'hybridNodeCount') },
    { key: 'hSsd', header: 'Hybrid SSD', align: 'right', render: (q) => drive(q, 'hybridVpodSsdCapacity') },
    { key: 'hdd', header: 'HDD Size', align: 'right', render: (q) => drive(q, 'hybridHddCapacity') },
    { key: 'aCount', header: 'AFA Server Count', align: 'center', render: (q) => count(q, 'afaNodeCount') },
    { key: 'aSsd', header: 'AFA SSD', align: 'right', render: (q) => drive(q, 'afaVpodSsdCapacity') },
  ];
  if (!showTotal) return base;
  return [...base, {
    key: 'total',
    header: 'Total Cost',
    align: 'right',
    // QuotesModal.js:155 — `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`.
    // NOT formatMoney: that prints two decimals and the classic prints none.
    render: (q) => `$${Number(q.metrics.totalSolutionCost ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
  }];
}

/** Faithful port of QuotesModal: same query (via useSavedQuotes), same 13
 *  columns, same row-click load. CONF-59 (search, sort, filter) is a separate
 *  ticket. */
export function LoadDialog() {
  const { loadSavedConfig, showQuotesModal, setShowQuotesModal, userName } = useQuoteActionsCtx();
  const ability = useAbility();
  const { quotes, loading, error } = useSavedQuotes(showQuotesModal);

  const close = () => setShowQuotesModal(false);
  const choose = (q: SavedQuote) => {
    // loadSavedConfig's 2nd positional arg is call-shape parity with the
    // classic QuotesModal.js call site only — useConfigPersistence.js takes it
    // as `_systemType` and reads savedConfig.systemType off q.config instead.
    // Harmless; kept (not removed) so the two call sites stay symmetric.
    const systemType = (cfg(q, 'systemType') as string | undefined) ?? '';
    loadSavedConfig(q.config, systemType, q.raw);
    close();
  };

  return (
    <Viewer open={showQuotesModal} onClose={close} title="Load configuration">
      {error && (
        <p role="alert" className="mb-3 rounded-[var(--radius-2)] border border-danger/50 bg-danger/10 px-3 py-2 font-body text-sm text-danger">
          {error}
        </p>
      )}
      {loading && <p className="font-body text-sm text-ink-muted">Loading your saved quotes…</p>}
      {!loading && !error && quotes.length === 0 && <p className="font-body text-sm text-ink-muted">You have no saved quotes yet.</p>}
      {quotes.length > 0 && (
        <DataTable
          caption="Saved quotes"
          columns={buildColumns(ability.can(PERM.LOAD_TOTAL_COST), userName)}
          rows={quotes}
          getRowKey={(q) => q.quoteId}
          rowClassName={() => 'cursor-pointer'}
          onRowClick={choose}
          // role="button" is gone from DataTable's row, so the row needs its
          // own name — the description plus the created date is what actually
          // identifies one quote to a rep choosing between several.
          rowAriaLabel={(q) => `${q.description} — ${q.createdAt.toLocaleDateString()}`}
        />
      )}
    </Viewer>
  );
}
